import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { filter, tap } from 'rxjs/operators';
import { map } from 'rxjs';
import { USER_PREFERENCES_REDUCER } from '../reducers/user-preferences.reducer';
import { REPORT_VIEWS_ACTIONS, USER_PREFERENCES_ACTIONS } from '../actions/user-preferences.actions';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';

@Injectable()
export class ReportViewsEffects {
  constructor(
    private readonly actions: Actions,
    private readonly store: Store,
    private readonly messageService: MessageService,
  ) {}

  createView$ = createEffect(() =>
    this.actions.pipe(
      ofType(REPORT_VIEWS_ACTIONS.create),
      concatLatestFrom(({ viewKey }) => this.store.select(USER_PREFERENCES_REDUCER.selectPreferenceByKey(viewKey))),
      filter(([, views]) => Boolean(views)),
      map(([{ view, viewKey }, views]) =>
        USER_PREFERENCES_ACTIONS.update({
          data: { [viewKey]: [...views!.filter(({ label }) => label !== view.label), view] },
        }),
      ),
      tap(() => this.messageService.add(CREATE_VIEW_SUCCESS_MESSAGE)),
    ),
  );

  deleteView$ = createEffect(() =>
    this.actions.pipe(
      ofType(REPORT_VIEWS_ACTIONS.delete),
      concatLatestFrom(({ viewKey }) => this.store.select(USER_PREFERENCES_REDUCER.selectPreferenceByKey(viewKey))),
      filter(([, views]) => Boolean(views)),
      map(([{ label: targetLabel, viewKey }, views]) =>
        USER_PREFERENCES_ACTIONS.update({
          data: { [viewKey]: views!.filter(({ label }) => label !== targetLabel) },
        }),
      ),
      tap(() => this.messageService.add(DELETE_VIEW_SUCCESS_MESSAGE)),
    ),
  );

  renameView$ = createEffect(() =>
    this.actions.pipe(
      ofType(REPORT_VIEWS_ACTIONS.rename),
      concatLatestFrom(({ viewKey }) => this.store.select(USER_PREFERENCES_REDUCER.selectPreferenceByKey(viewKey))),
      filter(([, views]) => Boolean(views)),
      map(([{ currentLabel, newLabel, viewKey }, views]) =>
        USER_PREFERENCES_ACTIONS.update({
          data: {
            [viewKey]: views!.map((view) => (view.label === currentLabel ? { ...view, label: newLabel } : view)),
          },
        }),
      ),
      tap(() => this.messageService.add(RENAME_VIEW_SUCCESS_MESSAGE)),
    ),
  );

  updateView$ = createEffect(() =>
    this.actions.pipe(
      ofType(REPORT_VIEWS_ACTIONS.update),
      concatLatestFrom(({ viewKey }) => this.store.select(USER_PREFERENCES_REDUCER.selectPreferenceByKey(viewKey))),
      filter(([, views]) => Boolean(views)),
      map(([{ view: updatingView, viewKey }, views]) =>
        USER_PREFERENCES_ACTIONS.update({
          data: {
            [viewKey]: views!.map((view) => (updatingView.label === view.label ? { ...updatingView } : view)),
          },
        }),
      ),
      tap(() => this.messageService.add(UPDATE_VIEW_SUCCESS_MESSAGE)),
    ),
  );
}

export const DELETE_VIEW_SUCCESS_MESSAGE = {
  severity: 'success',
  summary: 'Removed',
  detail: 'Template view has been removed.',
};
export const UPDATE_VIEW_SUCCESS_MESSAGE = {
  severity: 'success',
  summary: 'Updated',
  detail: 'Template view has been updated.',
};
export const CREATE_VIEW_SUCCESS_MESSAGE = {
  severity: 'success',
  summary: 'Saved',
  detail: 'Template view has been saved.',
};

export const RENAME_VIEW_SUCCESS_MESSAGE = {
  severity: 'success',
  summary: 'Renamed',
  detail: 'Template view has been renamed.',
};
